
import type { TEditorConfiguration } from "@/app/admin/_common/email-builder/documents/editor/core";
import { getCsrfToken } from "@/app/_utils/csrf";

export type AutomationTemplate = {
  id: string;
  key: string;
  channel: "email" | "sms";
  subject: string;
  body: string;
  text_body: string;
  // The email builder's block document. `body` is the HTML it renders to and what sends.
  body_json?: TEditorConfiguration | null;
};

export type Automation = {
  id: string;
  name: string;
  slug: string;
  description: string;
  event_key: string;
  conditions: any;
  delay_seconds: number;
  action_type: string;
  action_config: Record<string, any>;
  status: "draft" | "active" | "paused" | "archived";
  mode: "shadow" | "live";
  dedupe_key_template: string;
  on_condition_error: "skip" | "proceed";
  log_skipped_runs: boolean;
  priority: number;
  is_system: boolean;
  allow_manual_trigger: boolean;
  templates: AutomationTemplate[];
  run_count: number;
  last_run_at: string | null;
  created_at: string;
  updated_at: string;
};

export type AutomationRun = {
  id: string;
  automation: string;
  automation_name: string;
  automation_slug: string;
  event_key: string;
  event_id: string;
  status:
    | "pending"
    | "scheduled"
    | "running"
    | "completed"
    | "skipped"
    | "failed"
    | "cancelled";
  context: any;
  dedupe_key: string;
  recipient: string;
  mode: string;
  action_type: string;
  execution_path: string;
  provider_message_id: string;
  delivered_at: string | null;
  opened_at: string | null;
  rendered_subject: string;
  rendered_preview: string;
  skip_reason: string;
  error: string;
  scheduled_for: string | null;
  completed_at: string | null;
  created_at: string;
};

/** Shape of GET automations/schema/ — see server/command/services/automation/schema.py */
export type ContextField = {
  path: string;
  label: string;
  type: string;
  group: string;
};

export type AutomationSchema = {
  events: {
    key: string;
    domain: string;
    label: string;
    description: string;
    implemented: boolean;
    default_dedupe_key: string;
    // The event payload. Documentation only -- handlers re-fetch, so none of these
    // names exist in the context a template or condition reads.
    fields: { path: string; label: string; type: string; required: boolean; sample: any }[];
    context_roots: string[];
    // What a template or condition can actually read, grouped by namespace.
    context_fields: ContextField[];
  }[];
  actions: {
    key: string;
    label: string;
    description: string;
    channel: string;
    event_keys: string[];
    config_fields: {
      name: string;
      label: string;
      type: string;
      required: boolean;
      options: any[] | null;
      default: any;
      help: string;
    }[];
  }[];
  operators: { key: string; label: string; value_type: string; unary: boolean }[];
  message_streams: { key: string; label: string }[];
  // Bare names the email actions inject on top of the namespaced context. Email only.
  legacy_flat_fields: { path: string; label: string }[];
  event_action_compat: Record<string, string[]>;
  statuses: string[];
  modes: string[];
  run_statuses: string[];
};

class AutomationService {
  csrfToken = getCsrfToken();
  baseUrl = `${import.meta.env.VITE_API_BASE_URL}`;

  private jsonHeaders() {
    return {
      "Content-Type": "application/json",
      "X-CSRFToken": getCsrfToken(),
    };
  }

  getAutomationsAsync = async (): Promise<Automation[]> => {
    const res = await fetch(`${this.baseUrl}automations/`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return await res.json();
  };

  getAutomationAsync = async (id: string): Promise<Automation> => {
    const res = await fetch(`${this.baseUrl}automations/${id}/`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return await res.json();
  };

  getSchemaAsync = async (): Promise<AutomationSchema> => {
    const res = await fetch(`${this.baseUrl}automations/schema/`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return await res.json();
  };

  createAutomationAsync = async (body: Partial<Automation>): Promise<Automation> => {
    const res = await fetch(`${this.baseUrl}automations/`, {
      method: "POST",
      body: JSON.stringify(body),
      headers: this.jsonHeaders(),
      credentials: "include",
    });
    if (!res.ok) throw await res.json();
    return await res.json();
  };

  updateAutomationAsync = async (
    id: string,
    body: Partial<Automation>
  ): Promise<Automation> => {
    const res = await fetch(`${this.baseUrl}automations/${id}/`, {
      method: "PUT",
      body: JSON.stringify(body),
      headers: this.jsonHeaders(),
      credentials: "include",
    });
    if (!res.ok) throw await res.json();
    return await res.json();
  };

  /**
   * Activate, pause, archive, or switch between shadow and live.
   *
   * This is the cutover control: flipping mode to "live" silences the legacy send,
   * flipping it back restores it, and neither needs a deploy.
   */
  setStatusAsync = async (
    id: string,
    body: { status?: string; mode?: string }
  ): Promise<Automation> => {
    const res = await fetch(`${this.baseUrl}automations/${id}/status/`, {
      method: "POST",
      body: JSON.stringify(body),
      headers: this.jsonHeaders(),
      credentials: "include",
    });
    if (!res.ok) throw await res.json();
    return await res.json();
  };

  deleteAutomationAsync = async (id: string) => {
    // Returns the raw Response: the server answers 204 for a real delete and 200 with a
    // message when it archived instead (an automation with run history is never
    // destroyed), and the caller needs to tell those apart.
    return await fetch(`${this.baseUrl}automations/${id}/`, {
      method: "DELETE",
      headers: { "X-CSRFToken": getCsrfToken() },
      credentials: "include",
    });
  };

  getRunsAsync = async (id: string, params?: { status?: string }): Promise<AutomationRun[]> => {
    const query = params?.status ? `?status=${encodeURIComponent(params.status)}` : "";
    const res = await fetch(`${this.baseUrl}automations/${id}/runs/${query}`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return await res.json();
  };

  getAllRunsAsync = async (params?: {
    status?: string;
    event_key?: string;
  }): Promise<AutomationRun[]> => {
    const search = new URLSearchParams();
    if (params?.status) search.set("status", params.status);
    if (params?.event_key) search.set("event_key", params.event_key);
    const query = search.toString();
    const res = await fetch(`${this.baseUrl}automations/runs/${query ? `?${query}` : ""}`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return await res.json();
  };

  getStatsAsync = async (id: string) => {
    const res = await fetch(`${this.baseUrl}automations/${id}/stats/`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    return await res.json();
  };

  triggerAsync = async (id: string, body: { context?: any; mode?: string }) => {
    const res = await fetch(`${this.baseUrl}automations/${id}/trigger/`, {
      method: "POST",
      body: JSON.stringify(body),
      headers: this.jsonHeaders(),
      credentials: "include",
    });
    if (!res.ok) throw await res.json();
    return await res.json();
  };

  saveTemplateAsync = async (id: string, body: Partial<AutomationTemplate>) => {
    const res = await fetch(`${this.baseUrl}automations/${id}/templates/`, {
      method: "POST",
      body: JSON.stringify(body),
      headers: this.jsonHeaders(),
      credentials: "include",
    });
    if (!res.ok) throw await res.json();
    return await res.json();
  };
}

export default new AutomationService();
